import { computed, reactive, shallowReactive, shallowRef, watch } from 'vue'
import pin2d8cf0 from '/@/assets/icons/pin-2d8cf0.svg'
import { MapDoodleType } from '/@/constants/map'
import { getRoot } from '/@/root'
import { MapDoodleEnum } from '/@/types/map-enum'
import { EFlightAreaType } from '../types/flight-area'
import { message } from 'ant-design-vue'
import Draw, { createBox, createRegularPolygon, DrawEvent } from 'ol/interaction/Draw'
import Modify, { ModifyEvent } from 'ol/interaction/Modify'
import Snap from 'ol/interaction/Snap'
import DragPan from 'ol/interaction/DragPan'
import CircleStyle from 'ol/style/Circle'
import Fill from 'ol/style/Fill'
import { Polygon, Point, LineString, Geometry } from 'ol/geom'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import GeoJSON from 'ol/format/GeoJSON'
import Feature from 'ol/Feature'
import arrowIcon from '/@/assets/icons/arrow-up-circle.svg'
import { Icon, Style, Stroke, Text } from 'ol/style'
import { UseLawnmower } from './use-lawnmower'
import { buildDock2MissionKmz } from './use-kml-generator'
import RegularShape from 'ol/style/RegularShape'
import { useMyStore } from '/@/store'
import { FlightParams } from '/@/types/flight-params'
import { FlightCameraSensor } from '../types/flight-camera-sensor'
import { RouteBearing } from '/@/types/route-bearing'
import GeometryFactory from 'jsts/org/locationtech/jts/geom/GeometryFactory'
import GeoJSONReader from 'jsts/org/locationtech/jts/io/GeoJSONReader'
import GeoJSONWriter from 'jsts/org/locationtech/jts/io/GeoJSONWriter'
import UnionOp from 'jsts/org/locationtech/jts/operation/union/UnionOp.js'
import { CameraAngle } from '/@/types/camera-angle'
import { CURRENT_CONFIG } from '/@/api/http/config'
import { useSharedMap } from '/@/components/station/map/use-shared-map'
import { useSharedMapGrid } from '/@/components/station/map/use-shared-map-grid'
import { useSharedMapInteractions } from '/@/components/station/map/use-shared-map-interactions'
import { unByKey } from 'ol/Observable'
import { EventsKey } from 'ol/events'

const routeLayerRef = shallowRef<VectorLayer | null>()
const routeBearingDataRef = shallowRef<RouteBearing | null>()
const polygonFeatureRef = shallowRef<Feature<Geometry> | null>()
const mouseToolCurrentTypeRef = shallowRef<MapDoodleType | null>()
const mouseModeRef = shallowRef<boolean | null>()

export function useMouseTool () {
  const root = getRoot()
  const useLawnmower = UseLawnmower()
  const store = useMyStore()

  const state = reactive({
    pinNum: 0,
    polylineNum: 0,
    PolygonNum: 0,
    draw: null as Draw | null,
    boundaryModify: null as Modify | null,
    directionArrowModify: null as Modify | null,
    snap: null,
    contourSnap: null,
    enableTrace: false as boolean,
    arrowLayer: null,
    directionDeg: 0,
    bearingDeg: 0,
    directionArrowLength: 0,
    calcVolume: false as boolean
  })

  const { setMapTo, detach, isLoading, loadingProgress, labelTextDrawStyle } = useSharedMap()
  const { enableDraw, remove, enableSelect, enableDragPan, setMapDragPan, setMapDragZoom, createBoundaryPayload, createFeatureBoundaryPayload, boundaryFeatures } = useSharedMapInteractions()
  const { endPaintCallback, showGridLayer, removeGridLayer, setHovered, generateGrid, gridNormalStyle, gridSelectedStyle, unionFeaturesJsts } = useSharedMapGrid()

  const gridLayer = new VectorLayer({
    style: gridNormalStyle
  })

  const flightParams = computed<FlightParams>(() => {
    return store.state?.flightParams
  })

  const flightCameraAngleRef = computed<CameraAngle>(() => {
    return store.state?.flightCameraAngle
  })

  const referenceHeight = computed<number>(() => { return store.state?.referenceHeight })

  const enable3d = computed<boolean>(() => { return store.state?.enable3d })

  const camera = computed<FlightCameraSensor>(() => { return store.state?.flightCamera })

  watch(flightParams.value, (params) => {
    drawRouteWithFlightParams(params)
  }, { immediate: true })

  watch(() => store.state?.gsdTarget, (val) => {
    if (!val) return
    if (!polygonFeatureRef.value) return
    if (val !== store.state?.gsd.height) {
      const polygon = polygonFeatureRef.value.getGeometry()
      const newAltitude = useLawnmower.calculateAltitudeFromGsd(val, camera.value, flightParams.value, polygon, referenceHeight.value)
      flightParams.value.flightAltitude = newAltitude
    }
  }, { immediate: true })

  const flightAreaColorMap = {
    [EFlightAreaType.DFENCE]: '#19be6b',
    [EFlightAreaType.NFZ]: '#ff0000',
  }

  // // Preserve arrow length when polyline moves
  // function updateArrowFromCenter (center, angle, length) {
  //   return [
  //     center,
  //     [
  //       center[0] + Math.cos(angle) * length,
  //       center[1] + Math.sin(angle) * length
  //     ]
  //   ]
  // }

  function lineIntersection (p1:number[], p2:number[], p3:number[], p4:number[]) {
    const den =
      (p1[0] - p2[0]) * (p3[1] - p4[1]) -
      (p1[1] - p2[1]) * (p3[0] - p4[0])

    if (den === 0) return null

    const x =
      ((p1[0] * p2[1] - p1[1] * p2[0]) * (p3[0] - p4[0]) -
      (p1[0] - p2[0]) * (p3[0] * p4[1] - p3[1] * p4[0])) / den

    const y =
      ((p1[0] * p2[1] - p1[1] * p2[0]) * (p3[1] - p4[1]) -
      (p1[1] - p2[1]) * (p3[0] * p4[1] - p3[1] * p4[0])) / den

    return [x, y]
  }

  function createArrowPoints (start:number[], angleRad:number, length:number) {
    const end = [
      start[0] + Math.cos(angleRad) * length,
      start[1] + Math.sin(angleRad) * length
    ]

    return [start, end]
  }

  function drawArrowFeature (start:number[], end:number[]) {
    const arrowSource = root?.$flightRouteArrowLayer.getSource() ?? undefined
    if (!arrowSource) return
    arrowSource.clear()
    const angleDeg = state.directionDeg
    const lineFeature = new Feature({ geometry: new LineString([start, end]) })
    lineFeature.setStyle([
      new Style({
        stroke: new Stroke({
          color: '#D28519',
          width: 3
        })
      }),
      // new Style({
      //   geometry: new Point(end),
      //   image: new RegularShape({
      //     points: 3,
      //     radius: 10,
      // rotation: angleDeg,
      //     fill: new Fill({ color: '#D28519' })
      //   })
      // })
    ])
    arrowSource.addFeature(lineFeature)
  }

  // set arrow on the center
  function drawArrowToCenter () {
    if (!polygonFeatureRef.value) return

    const polygon = polygonFeatureRef.value.getGeometry()
    const directionDeg = state.directionDeg
    const angleRad = directionDeg * 180 / Math.PI
    const center = polygon.getInteriorPoint().getCoordinates()

    const length = 1e7 // long enough to exceed polygon size

    const rayEnd = [
      center[0] + Math.cos(angleRad) * length,
      center[1] + Math.sin(angleRad) * length,
    ]

    const ring = polygon.getCoordinates()[0] // outer ring

    let minDistance = Infinity

    for (let i = 0; i < ring.length - 1; i++) {
      const p = lineIntersection(
        center,
        rayEnd,
        ring[i],
        ring[i + 1]
      )

      if (!p) continue

      const dx = p[0] - center[0]
      const dy = p[1] - center[1]

      // Must be forward along ray
      if (dx * Math.cos(angleRad) + dy * Math.sin(angleRad) <= 0) continue

      const dist = Math.sqrt(dx * dx + dy * dy)
      minDistance = Math.min(minDistance, dist)
    }

    const directionArrowLength = minDistance + 100
    const [start, end] = createArrowPoints(center, angleRad, directionArrowLength)
    drawArrowFeature(start, end)
  }

  function drawRoute (
    flightParams: FlightParams,
    polygon: Polygon,
    directionDeg:number,
    bearingDeg: number,
    flightCameraAngle: CameraAngle,
  ) {
    if (routeLayerRef.value) {
      routeLayerRef.value?.getSource().clear()
      root?.$map.removeLayer(routeLayerRef.value)
    }

    const routeSource = new VectorSource()
    const routeLayer = new VectorLayer({ source: routeSource })
    root?.$map.addLayer(routeLayer)
    routeLayerRef.value = routeLayer

    const flightCamera:FlightCameraSensor = camera.value
    const refHeight = referenceHeight.value

    const flightAltitude = flightParams.flightAltitude
    if (flightParams.targetGsdAltitude <= -13000000) {
      flightParams.targetGsdAltitude = refHeight
    }

    const gsd = useLawnmower.calculateGsd(flightAltitude, flightCamera, flightParams, refHeight)
    store.commit('SET_GSD_INFO', gsd)

    const footprint = useLawnmower.calculateFootprint(gsd, flightCamera, flightParams)

    const routeBearing = useLawnmower.generateLawnmowerPath(
      flightParams,
      polygon,
      directionDeg,
      bearingDeg,
      gsd,
      footprint,
      flightCameraAngle,
    )

    const routeInfo = useLawnmower.calculateRouteInfo(routeBearing.routePointBearings, flightParams, footprint)
    store.commit('SET_ROUTE_INFO', routeInfo)

    const routeWithBearing = routeBearing.routePointBearings
    routeBearingDataRef.value = routeBearing

    const surfaceFollowEnable = flightParams.surfaceFollowModeEnable
    // console.log(routeBearingDataRef.value)

    // Draw points
    if (enable3d.value === false) {
      routeWithBearing.forEach((wp, i) => {
        const ptindex = wp.index + 1
        const ptFeature = new Feature({ geometry: new Point(wp.coordinate), waypointIndex: ptindex, heading: wp.bearing })
        const bearingRad = wp.bearing * Math.PI / 180
        ptFeature.setStyle([
          new Style({
            image: new CircleStyle({
              radius: 3,
              fill: new Fill({ color: 'orange' }),
              stroke: new Stroke({ color: 'white', width: 1 })
            })
          }),
          // arrow
          new Style({
            image: new Icon({
              src: arrowIcon,
              rotation: bearingRad, // radians
              rotateWithView: true,
              scale: 0.2
            })
          }),
          // index text
          new Style({
            text: new Text({
              // text: String(ptindex) + ' (' + String(wp.altitude.toFixed(2)) + ', ' + String(wp.bearing.toFixed(2)) + ')',
              text: String(ptindex) + ' (' + String(Math.round(wp.altitude)) + ')',
              font: 'bold 11px sans-serif',
              fill: new Fill({ color: '#fff' }),
              stroke: new Stroke({ color: '#000', width: 2 }),
              textAlign: 'center',
              textBaseline: 'middle',
              offsetY: -8
            })
          })
        ])
        routeSource.addFeature(ptFeature)
      })
    }

    // Draw line
    if (routeWithBearing.length > 1) {
      const line = new Feature({
        geometry: new LineString(
          routeWithBearing.map((wp) => {
            if (surfaceFollowEnable === true) {
              return [wp.coordinate[0], wp.coordinate[1], wp.altitude]
            } else {
              return [wp.coordinate[0], wp.coordinate[1], refHeight + wp.altitude]
            }
          })
        )
      })
      routeSource.addFeature(line)
    }
  }

  function drawRouteWithFlightParams (params:FlightParams) {
    if (!polygonFeatureRef.value) return
    if (params.safeClearance < 1) return
    if (params.flightAltitude < params.safeClearance) return

    const polygon = polygonFeatureRef.value.getGeometry()
    const bearingDeg = state.bearingDeg
    const directionDeg = state.directionDeg
    const routeLineDeg = directionDeg
    const flightCameraAngle = flightCameraAngleRef.value

    drawRoute(params, polygon, routeLineDeg, bearingDeg, flightCameraAngle)
  }

  // --- Update route visualization ---
  function updateRoute () {
    drawRouteWithFlightParams(flightParams.value)
  }

  // // --- Visualization ---
  // function visualizeRoute (feature) {
  //   // if(!polygonFeature) return alert("Draw a polygon first!");
  //   const poly = feature.getGeometry() as Polygon
  //   const routePoints = generateLawnmower(poly)
  //   console.log('Generated route points:', routePoints)

  //   const features = routePoints.map((p, i) => new Feature({ geometry: new Point(p), waypointIndex: i + 1 }))
  //   const line = new Feature({ geometry: new LineString(routePoints) })

  //   features.forEach(f => f.setStyle(new Style({ image: new CircleStyle({ radius: 5, fill: new Fill({ color: 'red' }), stroke: new Stroke({ color: 'white', width: 2 }) }) })))
  //   line.setStyle(new Style({ stroke: new Stroke({ color: 'blue', width: 2 }) }))

  //   if (routeLayerRef.value) root?.$map.removeLayer(routeLayerRef.value)
  //   const routeLayer = new VectorLayer({ source: new VectorSource({ features: [line, ...features] }) })
  //   root?.$map.addLayer(routeLayer)
  //   routeLayerRef.value = routeLayer

  //   // Store route globally for JSON export
  //   // (window as any).routePoints = routePoints
  // }

  function setDragPanActive (active: boolean) {
    root?.$map.getInteractions().forEach((interaction:any) => {
      if (interaction instanceof DragPan) {
        interaction.setActive(active)
      }
    })
  }

  function onDirectionArrowModifyModifyEnd (e:ModifyEvent) {
    e.features.forEach((feature: Feature) => {
      const geom = feature.getGeometry() as LineString
      const [start, end] = geom.getCoordinates()

      const dx = end[0] - start[0]
      const dy = end[1] - start[1]
      const length = Math.hypot(dx, dy)

      const angleRad = Math.atan2(dy, dx)
      const angleDeg = angleRad * 180 / Math.PI

      state.directionArrowLength = length
      state.directionDeg = angleDeg

      drawArrowFeature(start, end)
      updateRoute()
    })
  }
  function onBoundaryModifyEnd () {
    updateRoute()
  }
  function enableModify () {
    const flightRouteBoundaryLayerSource = root?.$flightRouteBoundaryLayer.getSource()
    if (!flightRouteBoundaryLayerSource) return

    // route direction modify
    if (state.directionArrowModify) {
      root?.$map.removeInteraction(state.directionArrowModify)
    }

    const directionArrowModify = new Modify({
      source: root?.$flightRouteArrowLayer.getSource() ?? undefined,
      condition: e => {
        return e.originalEvent.buttons === 1
      },
      // Disable inserting new vertices
      insertVertexCondition: () => false,
    })
    // Prevent inserting new vertices
    // directionArrowModify.set('insertVertexCondition', () => false)

    directionArrowModify.on('modifyend', onDirectionArrowModifyModifyEnd)
    state.directionArrowModify = directionArrowModify
    root?.$map.addInteraction(directionArrowModify)

    // boundary Polygon modify
    if (state.boundaryModify) {
      root?.$map.removeInteraction(state.boundaryModify)
    }

    const contourSource = root?.$contourLineLayer10.getSource() ?? undefined

    const boundaryModify = new Modify({
      source: flightRouteBoundaryLayerSource,
      trace: true,
      traceSource: contourSource,
      hitTolerance: 15, // 🔥 REQUIRED
      style: new Style({
        image: new CircleStyle({
          radius: 8, // BIG hit area
          fill: new Fill({ color: 'white' }),
          stroke: new Stroke({
            color: 'blue',
            width: 2,
          }),
        }),
      }),
    })

    boundaryModify.on('modifyend', onBoundaryModifyEnd)
    state.boundaryModify = boundaryModify

    root?.$map.addInteraction(boundaryModify)
  }

  function onDrawEndHandler (e:DrawEvent) {
    if (mouseToolCurrentTypeRef.value !== MapDoodleEnum.MULTIPOLYGON) {
      const feature = e.feature
      feature.set('altitudeMode', 'clampToGround')
      polygonFeatureRef.value = feature
      drawArrowToCenter()
      updateRoute()
    } else if (state.calcVolume) {
      const feature = e.feature
      drawVolumeCallback(feature)
    }
  }

  function addInteraction (type: any, geometryFunction, getDrawCallback:Function,) {
    if (state.draw) {
      state.draw.setActive(false)
      root?.$map.removeInteraction(state.draw)
    }

    if (state.snap) {
      state.snap.setActive(false)
      root?.$map.removeInteraction(state.snap)
    }
    if (state.contourSnap) {
      state.contourSnap.setActive(false)
      root?.$map.removeInteraction(state.contourSnap)
    }

    enableModify()

    const drawSource = root?.$flightRouteBoundaryLayer.getSource() ?? undefined
    const contourSource = root?.$contourLineLayer10.getSource() ?? undefined
    const enableTrace = state.enableTrace
    const draw = new Draw({
      source: drawSource,
      type: type,
      trace: enableTrace,
      traceSource: contourSource,
      geometryFunction: geometryFunction,
    })

    state.draw = draw
    root?.$map.addInteraction(draw)

    if (enableTrace === true) {
      const snap = new Snap({ source: drawSource })
      state.snap = snap
      root?.$map.addInteraction(snap)

      const contourSnap = new Snap({ source: contourSource })
      state.contourSnap = contourSnap
      root?.$map.addInteraction(contourSnap)
    }

    root?.$map.on('drawend', getDrawCallback)

    // 🔥 IMPORTANT: only once
    draw.on('drawend', onDrawEndHandler)
  }

  function drawClear () {
    root?.$flightRouteBoundaryLayer?.getSource().clear()
    root?.$flightRouteArrowLayer?.getSource().clear()
    const routeLayer = routeLayerRef.value

    routeLayer?.getSource()?.clear()
    root?.$map.removeLayer(routeLayer)
    routeLayerRef.value = null
    routeBearingDataRef.value = null
  }

  function drawSave () {
    console.log('saving route')
    console.log(routeBearingDataRef.value)
    if (routeBearingDataRef.value !== null) {
      buildDock2MissionKmz(routeBearingDataRef.value)
    }
  }

  function drawPin (type:MapDoodleType, getDrawCallback:Function) {
    addInteraction('Point', undefined, getDrawCallback)
    // addInteraction('Circle', createRegularPolygon(4), getDrawCallback)
    // root?.$mouseTool = new Draw({
    //   source: root?.$flightRouteBoundaryLayer.getSource() ?? undefined,
    //   type: type,
    // })
    // root?.$map.on('drawend', getDrawCallback)
    // root?.$mouseTool.marker({
    //   title: type + state.pinNum,
    //   icon: pin2d8cf0,
    // })
    // state.pinNum++
    // root?.$mouseTool.on('draw', getDrawCallback)
  }

  function drawPolyline (type:MapDoodleType, getDrawCallback:Function) {
    addInteraction('MultiLineString', undefined, getDrawCallback)
    // root?.$mouseTool.polyline({
    //   strokeColor: '#2d8cf0',
    //   strokeOpacity: 1,
    //   strokeWeight: 2,
    //   strokeStyle: 'solid',
    //   title: type + state.polylineNum++
    // })
    // root?.$mouseTool.on('draw', getDrawCallback)
  }

  function drawPolygon (type:MapDoodleType, getDrawCallback:Function) {
    addInteraction('Polygon', undefined, getDrawCallback)
    // root?.$mouseTool.polygon({
    //   strokeColor: '#2d8cf0',
    //   strokeOpacity: 1,
    //   strokeWeight: 2,
    //   fillColor: '#1791fc',
    //   fillOpacity: 0.4,
    //   title: type + state.PolygonNum++
    // })
    // root?.$mouseTool.on('draw', getDrawCallback)
  }

  function drawMultiPolygon (type:MapDoodleType, getDrawCallback:Function) {
    addInteraction('MultiPolygon', undefined, getDrawCallback)
  }

  function drawVolume (type:MapDoodleType, getDrawCallback:Function) {
    state.calcVolume = true
    root?.$flightRouteBoundaryLayer.setStyle(boundaryStyle)
    addInteraction('MultiPolygon', undefined, getDrawCallback)
  }

  function redrawBoundaryLayer (features: Feature<Geometry>[]) {
    const feature = unionFeaturesJsts(features)
    polygonFeatureRef.value = feature
    const drawSource = root?.$flightRouteBoundaryLayer.getSource() ?? undefined
    drawSource.clear()
    drawSource.addFeature(feature)

    drawArrowToCenter()
    updateRoute()
  }

  function onDrawGridCallback () {
    console.log('drawGridCallback')
    if (boundaryFeatures.value === null) return
    const features = boundaryFeatures.value

    redrawBoundaryLayer(features)
  }

  function drawGrid (type:MapDoodleType, getDrawCallback:Function) {
    mouseModeRef.value = true
    setMapDragPan(false)
    setMapDragZoom(false)
    setMapTo(root?.$map)
    endPaintCallback.value = onDrawGridCallback
    showGridLayer()
  }

  // Style function
  function boundaryStyle (feature) {
    const label = feature.get('labelText') || ''

    return new Style({
      fill: new Fill({
        color: 'rgba(0, 120, 255, 0.2)'
      }),
      stroke: new Stroke({
        color: '#0078ff',
        width: 2
      }),
      text: new Text({
        text: label,
        font: '18px sans-serif',
        fill: new Fill({ color: '#111' }),
        stroke: new Stroke({ color: '#fff', width: 3 }),
        textAlign: 'center',
        textBaseline: 'middle',
        overflow: true
      })
    })
  }

  // Load JSON and put the content into the polygon
  async function loadVolumeLabel (layer, jsonUrl) {
    layer.setStyle(boundaryStyle)
    const drawSource = layer.getSource()

    try {
      const res = await fetch(jsonUrl)

      // const text = await res.text() // get raw text first
      // console.log('Volume Json Response body:', text) // ← look at this in console

      // const data = JSON.parse(text) // then parse
      const data = await res.json()

      const shape = data.shapes?.[0] // first shape
      if (!shape) return

      // Create multiline text
      const labelText = [
        `Label: ${shape.label}`,
        `Area: ${shape.area.toLocaleString()} m²`,
        `Above: ${shape.above.toLocaleString()} m³`,
        `Below: ${shape.below.toLocaleString()} m³`,
        `Net: ${shape.net.toLocaleString()} m³`
      ].join('\n')

      // Put the text into the feature(s)
      const features = drawSource.getFeatures()

      if (features.length === 1) {
        // Only one polygon → just set it
        features[0].set('labelText', labelText)
      } else {
        // Multiple polygons → try match by label
        features.forEach(f => {
          const featureLabel = f.get('label') || f.get('name') || f.getId()
          if (featureLabel === shape.label) {
            f.set('labelText', labelText)
          }
        })
      }

      // Force redraw
      layer.changed()
    } catch (err) {
      console.error('Failed to load volume JSON:', err)
    }
  }

  async function drawVolumeCallback (feature: Feature) {
    const format = new GeoJSON()

    const reader = new GeoJSONReader(new GeometryFactory())
    const writer = new GeoJSONWriter()

    let unionedJstsGeom:any = null

    const olGeom = feature.getGeometry()
    const jstsGeom = reader.read(
      format.writeGeometryObject(olGeom)
    )

    unionedJstsGeom = jstsGeom

    const geoJsonStr = JSON.stringify(
      writer.write(unionedJstsGeom)
    )

    const payload = {
      boundary_geojson: geoJsonStr,
      crs: 'EPSG:3857'
    }

    console.log(payload)
    try {
      const prefix = CURRENT_CONFIG.dwcURL
      const response = await fetch(prefix + '/dwcapp/api/v1/task/metashape-clip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      const result = await response.json()

      const volumeFile = result.clip_task_id + '-volume.json'
      const downloadUrl = result.clip_path + '/' + volumeFile
      for (let attempt = 1; attempt <= 10; attempt++) {
        message.info(`Calculating volume ${attempt * 10}%`)
        const resp = await fetch(downloadUrl, { method: 'HEAD' }) // lighter than GET

        if (resp.ok) {
          // Apply style
          const res = await fetch(downloadUrl)
          const data = await res.json()

          const shape = data.shapes?.[0] // first shape
          if (!shape) return

          // Create multiline text
          const labelText = [
            `Label: ${shape.label}`,
            `Area: ${shape.area.toLocaleString()} m²`,
            `Above: ${shape.above.toLocaleString()} m³`,
            `Below: ${shape.below.toLocaleString()} m³`,
            `Net: ${shape.net.toLocaleString()} m³`
          ].join('\n')

          feature.set('labelText', labelText)
          feature.changed()
          return
        }

        await new Promise(resolve => setTimeout(resolve, 1000))
      }
    } catch (err:any) {
      alert('Failed to send clip task: ' + err.message)
    }
  }

  async function drawClip () {
    const drawSource = root?.$flightRouteBoundaryLayer.getSource() ?? undefined
    if (!drawSource) return

    const format = new GeoJSON()
    // const geoJsonStr = format.writeFeatures(drawSource?.getFeatures())

    const reader = new GeoJSONReader(new GeometryFactory())
    const writer = new GeoJSONWriter()

    let unionedJstsGeom:any = null
    drawSource?.getFeatures().forEach((feature:Feature, index:number) => {
      const olGeom = feature.getGeometry()
      const jstsGeom = reader.read(
        format.writeGeometryObject(olGeom)
      )

      if (index === 0) {
        unionedJstsGeom = jstsGeom
      } else {
        // True union — no even/odd holes!
        unionedJstsGeom = UnionOp.union(unionedJstsGeom, jstsGeom)
      }
    })

    const geoJsonStr = JSON.stringify(
      writer.write(unionedJstsGeom)
    )

    const payload = {
      boundary_geojson: geoJsonStr,
      crs: 'EPSG:3857'
    }

    try {
      const prefix = CURRENT_CONFIG.dwcURL
      const response = await fetch(prefix + '/dwcapp/api/v1/task/metashape-clip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      const result = await response.json()

      // drawSource.clear()
      drawOff(MapDoodleEnum.MULTIPOLYGON)

      const orthoFile = result.clip_task_id + '-orthomosaic.tif'
      const downloadUrl = result.clip_path + '/' + orthoFile
      for (let attempt = 1; attempt <= 10; attempt++) {
        message.info(`Building file task ${result.clip_task_id} ${attempt * 10}%`)
        const resp = await fetch(downloadUrl, { method: 'HEAD' }) // lighter than GET

        if (resp.ok) {
          // Apply style
          loadVolumeLabel(root?.$flightRouteBoundaryLayer, downloadUrl)

          const baseFileUrl = result.clip_base_url + result.clip_path + '/' + result.clip_task_id
          const crs = result.clip_coord_name // baseFileUrl + '-crs.prj'
          const siteId = result.clip_site_id
          // const siteName = result.clip_site_name
          const pcdUrl = baseFileUrl + '-point-cloud.pts'
          const tifUrl = baseFileUrl + '-orthomosaic.tif'
          window.location.href = `eagledesign://terrain?v=1&siteId=${siteId}&pcd=${pcdUrl}&tif=${tifUrl}`
          return
        }

        await new Promise(resolve => setTimeout(resolve, 5000))
      }

      window.location.href = result.clip_path
    } catch (err:any) {
      alert('Failed to send clip task: ' + err.message)
    }
  }

  async function drawBoundary () {
    const drawSource = root?.$flightRouteBoundaryLayer.getSource() ?? undefined
    if (!drawSource) return

    const format = new GeoJSON()
    // const geoJsonStr = format.writeFeatures(drawSource?.getFeatures())

    const reader = new GeoJSONReader(new GeometryFactory())
    const writer = new GeoJSONWriter()

    let unionedJstsGeom:any = null
    drawSource?.getFeatures().forEach((feature:Feature, index:number) => {
      const olGeom:any = feature.getGeometry()?.clone().transform('EPSG:3857', 'EPSG:4326')
      const jstsGeom = reader.read(
        format.writeGeometryObject(olGeom)
      )

      if (index === 0) {
        unionedJstsGeom = jstsGeom
      } else {
        // True union — no even/odd holes!
        unionedJstsGeom = UnionOp.union(unionedJstsGeom, jstsGeom)
      }
    })

    if (unionedJstsGeom == null) {
      message.info('Cannot get selected geometry. Please try again!')
    }

    const payload = JSON.stringify(
      writer.write(unionedJstsGeom)
    )

    try {
      const response = await fetch('/dwcapp/api/v1/survey/boundary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      const result = await response.json()
      message.info(`Post Survey Boundary ${result.status} ${result.message}`)
    } catch (err:any) {
      alert('Failed to set boundary: ' + err.message)
    } finally {
      drawSource.clear()
      drawOff(MapDoodleEnum.MULTIPOLYGON)
    }
  }

  function drawOff (type:MapDoodleType) {
    state.directionArrowModify?.un('modifyend', onDirectionArrowModifyModifyEnd)
    state.boundaryModify?.un('modifyend', onBoundaryModifyEnd)
    state.draw?.un('drawend', onDrawEndHandler)

    state.calcVolume = false
    state.boundaryModify?.setActive(false)
    if (state.snap) {
      state.snap?.setActive(false)
      root?.$map.removeInteraction(state.snap)
    }

    if (state.contourSnap) {
      state.contourSnap?.setActive(false)
      root?.$map.removeInteraction(state.contourSnap)
    }

    state.draw?.setActive(false)
    // setDragPanActive(true)
    // root?.$mouseTool.close()
    // root?.$mouseTool.off('draw')

    removeGridLayer()
    setMapDragPan(true)
    mouseModeRef.value = false
  }

  function drawFlightAreaPolygon (type: EFlightAreaType, getDrawFlightAreaCallback: Function) {
    addInteraction('Polygon', undefined, getDrawFlightAreaCallback)
    // root?.$mouseTool.polygon({
    //   strokeColor: flightAreaColorMap[type],
    //   strokeOpacity: 1,
    //   strokeWeight: 4,
    //   extData: {
    //     type: type,
    //     mapType: 'polygon',
    //   },
    //   strokeStyle: 'dashed',
    //   strokeDasharray: EFlightAreaType.NFZ === type ? [10, 2] : [10, 1, 2],
    //   fillColor: flightAreaColorMap[type],
    //   fillOpacity: EFlightAreaType.NFZ === type ? 0.3 : 0,
    // })
    // root?.$mouseTool.on('draw', getDrawFlightAreaCallback)
  }

  function drawFlightAreaCircle (type: EFlightAreaType, getDrawFlightAreaCallback: Function) {
    addInteraction('Circle', undefined, getDrawFlightAreaCallback)
    // root?.$mouseTool.circle({
    //   strokeColor: flightAreaColorMap[type],
    //   strokeOpacity: 1,
    //   strokeWeight: 6,
    //   extData: {
    //     type: type,
    //     mapType: 'circle',
    //   },
    //   strokeStyle: 'dashed',
    //   strokeDasharray: EFlightAreaType.NFZ === type ? [10, 2] : [10, 1, 2],
    //   fillColor: flightAreaColorMap[type],
    //   fillOpacity: EFlightAreaType.NFZ === type ? 0.3 : 0,
    // })
    // root?.$mouseTool.on('draw', getDrawFlightAreaCallback)
  }

  function mouseTool (type: MapDoodleType, getDrawCallback: Function, flightAreaType?: EFlightAreaType) {
    mouseToolCurrentTypeRef.value = type
    if (flightAreaType) {
      switch (type) {
        case MapDoodleEnum.POLYGON:
          drawFlightAreaPolygon(flightAreaType, getDrawCallback)
          return
        case MapDoodleEnum.CIRCLE:
          drawFlightAreaCircle(flightAreaType, getDrawCallback)
          return
        default:
          message.error(`Invalid type: ${flightAreaType}`)
          return
      }
    }
    switch (type) {
      case MapDoodleEnum.PIN:
        drawPin(type, getDrawCallback)
        break
      case MapDoodleEnum.POLYLINE:
        drawPolyline(type, getDrawCallback)
        break
      case MapDoodleEnum.POLYGON:
        drawPolygon(type, getDrawCallback)
        break
      case MapDoodleEnum.MULTIPOLYGON:
        drawMultiPolygon(type, getDrawCallback)
        break
      case MapDoodleEnum.CLIP:
        drawMultiPolygon(type, getDrawCallback)
        break
      case MapDoodleEnum.CLIPOK:
        drawClip()
        break
      case MapDoodleEnum.BOUNDARY:
        drawMultiPolygon(type, getDrawCallback)
        break
      case MapDoodleEnum.BOUNDARYOK:
        drawBoundary()
        break
      case MapDoodleEnum.VOLUME:
        drawVolume(type, getDrawCallback)
        break
      case MapDoodleEnum.GRID:
        drawGrid(type, getDrawCallback)
        break
      case MapDoodleEnum.CLEAR:
        drawClear()
        break
      case MapDoodleEnum.SAVE:
        drawSave()
        break
      case MapDoodleEnum.Close:
        drawOff(type)
        break
    }
  }

  function setEnableTrace (val:boolean) {
    state.enableTrace = val
  }

  return {
    routeLayerRef,
    routeBearingDataRef,
    mouseToolCurrentTypeRef,
    mouseModeRef,
    mouseTool,
    redrawBoundaryLayer,
    setEnableTrace
  }
}
